/**
 * Sanitização de HTML de campanha antes de pintar com `dangerouslySetInnerHTML`.
 *
 * O backend já filtra na gravação e na leitura pública (`PublicHtmlSanitizer`).
 * Esta é a última camada, no browser: remove elementos executáveis, todo atributo
 * fora da allowlist (inclusive `on*`) e URLs com esquema que não seja http(s)/relativo.
 * Não reescreve a estrutura do conteúdo — negrito, listas, links e imagens legadas continuam.
 */

const REMOVED_ELEMENTS = new Set([
  'script',
  'style',
  'iframe',
  'frame',
  'frameset',
  'object',
  'embed',
  'applet',
  'link',
  'meta',
  'base',
  'form',
  'input',
  'button',
  'textarea',
  'select',
  'option',
  'svg',
  'math',
  'template',
  'noscript',
  'title',
  'head',
])

const ALLOWED_ATTRIBUTES = new Set([
  'href',
  'src',
  'alt',
  'title',
  'target',
  'rel',
  'class',
  'style',
  'width',
  'height',
  'colspan',
  'rowspan',
  'align',
  'loading',
])

const URL_ATTRIBUTES = new Set(['href', 'src'])

const SAFE_URL_PATTERN = /^(?:https?:|mailto:|tel:|\/|#|\.\/|\.\.\/)/i
const SCHEME_PATTERN = /^[a-z][a-z0-9+.-]*:/i

/**
 * Remove controles e espaços que o browser ignora ao interpretar a URL
 * (`java\nscript:` vira `javascript:`), para a checagem de esquema ver o que o browser vê.
 */
function stripUrlNoise(value: string): string {
  let result = ''
  for (const char of value) {
    const code = char.charCodeAt(0)
    const isControlOrSpace = code <= 0x20 || (code >= 0x7f && code <= 0x9f)
    if (!isControlOrSpace) {
      result += char
    }
  }
  return result
}

function isSafeUrlAttribute(rawValue: string): boolean {
  const value = stripUrlNoise(rawValue)
  if (value === '') return false
  if (SAFE_URL_PATTERN.test(value)) return true

  return !SCHEME_PATTERN.test(value)
}

function isDangerousStyle(value: string): boolean {
  return /expression\s*\(|url\s*\(|javascript:|behavior\s*:|@import/i.test(value)
}

function sanitizeElement(element: Element): void {
  for (const attribute of Array.from(element.attributes)) {
    const name = attribute.name.toLowerCase()

    if (!ALLOWED_ATTRIBUTES.has(name)) {
      element.removeAttribute(attribute.name)
      continue
    }

    if (URL_ATTRIBUTES.has(name) && !isSafeUrlAttribute(attribute.value)) {
      element.removeAttribute(attribute.name)
      continue
    }

    if (name === 'style' && isDangerousStyle(attribute.value)) {
      element.removeAttribute(attribute.name)
    }
  }

  if (element.tagName === 'A' && element.getAttribute('target') === '_blank') {
    element.setAttribute('rel', 'noopener noreferrer')
  }
}

function sanitizeChildren(parent: Node): void {
  for (const child of Array.from(parent.childNodes)) {
    if (child.nodeType === Node.COMMENT_NODE) {
      parent.removeChild(child)
      continue
    }

    if (child.nodeType !== Node.ELEMENT_NODE) {
      continue
    }

    const element = child as Element
    if (REMOVED_ELEMENTS.has(element.tagName.toLowerCase())) {
      parent.removeChild(element)
      continue
    }

    sanitizeElement(element)
    sanitizeChildren(element)
  }
}

export function sanitizePublicHtml(html: string): string {
  if (html.trim() === '') return ''

  if (typeof DOMParser === 'undefined') {
    return ''
  }

  const document = new DOMParser().parseFromString(`<body>${html}</body>`, 'text/html')
  const body = document.body
  if (!body) return ''

  sanitizeChildren(body)

  return body.innerHTML
}
